import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { 
  Mail, 
  Phone, 
  Send, 
  MessageSquare,
  Linkedin,
  Instagram
} from "lucide-react";
import { XIcon } from "@/components/icons/XIcon";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

const Contact = () => {
  const { toast } = useToast();
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#contact-form") {
      document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
    });
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      detail: "support@thetechbus.com",
      description: "We'll respond within 24 hours",
      color: "from-violet-500 to-purple-600",
    },
    {
      icon: Phone,
      title: "Call Us",
      detail: "+91 8147199511",
      description: "Call or WhatsApp",
      color: "from-blue-500 to-cyan-600",
    },
  ];

  const socialLinks = [
    { icon: XIcon, href: "https://x.com/TheTechBus01", label: "X" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/thetechbusconnects", label: "LinkedIn" },
    { icon: Instagram, href: "https://www.instagram.com/the_tech_bus", label: "Instagram" },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-24 relative overflow-hidden px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto text-center relative z-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium mb-6 animate-fade-in">
              Contact Us
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6 animate-fade-in">
              Let's Build Something
              <span className="gradient-text block">Amazing Together</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in">
              Have a project in mind? We'd love to hear from you. Send us a message 
              and we'll respond as soon as possible.
            </p>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-2 gap-6 stagger-children max-w-2xl mx-auto">
              {contactInfo.map((info, index) => (
                <div
                  key={info.title}
                  className="p-6 rounded-2xl glass-card hover-lift text-center group"
                >
                  <div className={`w-14 h-14 mx-auto rounded-xl bg-gradient-to-br ${info.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <info.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold mb-1">{info.title}</h3>
                  <p className="text-foreground font-medium mb-1">
                    {info.title === "Call Us" ? (
                      <a href="tel:+918147199511" className="hover:text-primary transition-colors">{info.detail}</a>
                    ) : (
                      info.detail
                    )}
                  </p>
                  <p className="text-muted-foreground text-sm">{info.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section id="contact-form" className="py-16 px-4 sm:px-6 lg:px-8 xl:px-12 scroll-mt-24">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Form */}
              <div className="glass-card rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold">Send a Message</h2>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium mb-2">
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    >
                      <option value="">Select a topic</option>
                      <option value="general">General Inquiry</option>
                      <option value="project">Project Discussion</option>
                      <option value="support">Technical Support</option>
                      <option value="partnership">Partnership</option>
                      <option value="careers">Careers</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                      placeholder="Tell us about your project or question..."
                    />
                  </div>
                  
                  <Button type="submit" size="lg" className="w-full glow-primary bg-gradient-to-r from-primary to-accent hover:opacity-90">
                    Send Message
                    <Send className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </div>

              {/* Info Panel */}
              <div className="space-y-6">
                {/* Social Links */}
                <div className="glass-card rounded-3xl p-8">
                  <h3 className="text-xl font-heading font-bold mb-6">Connect With Us</h3>
                  <div className="flex gap-4">
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.href}
                        aria-label={social.label}
                        className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-all"
                      >
                        <social.icon className="w-5 h-5" />
                      </a>
                    ))}
                  </div>
                  <p className="text-muted-foreground text-sm mt-4">
                    Follow us for the latest updates, tech tips, and behind-the-scenes content.
                  </p>
                </div>

                {/* Quick Response */}
                <div className="rounded-3xl p-8 bg-gradient-to-br from-primary/10 via-purple-500/10 to-accent/10 border border-primary/20">
                  <h3 className="text-xl font-heading font-bold mb-2">Quick Response Guarantee</h3>
                  <p className="text-muted-foreground">
                    We pride ourselves on fast communication. Expect a response within 
                    24 hours on business days. For urgent matters, don't hesitate to call us directly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Teaser */}
        <section id="faq" className="py-16 px-4 sm:px-6 lg:px-8 xl:px-12 scroll-mt-24">
          <div className="container mx-auto text-center">
            <div className="glass-card rounded-3xl p-8 md:p-12 max-w-2xl mx-auto">
              <h2 className="text-2xl font-heading font-bold mb-4">Have Questions?</h2>
              <p className="text-muted-foreground mb-6">
                Check out our frequently asked questions or reach out directly.
              </p>
              <Button variant="outline" size="lg" className="glass" onClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })}>
                View FAQ
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
